import React, { useState, useEffect, useContext, useMemo } from 'react';
import { training } from 'src/backend';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import useTrainingComplete from './useTrainingComplete';
import useAuth from 'src/hooks/useAuth';
import { parseJsonObject } from 'src/utils/json';

const ProgressContext = React.createContext('progress');

export function ProgressProvider({ children }) {
  const [progress, setProgress] = useState();

  return (
    <ProgressContext.Provider value={{ progress, setProgress }}>
      {children}
    </ProgressContext.Provider>
  );
}

export default function useProgess() {
  const { user } = useAuth();
  const { t } = useTranslation('translation');
  const { enqueueSnackbar } = useSnackbar();
  const { progress, setProgress } = useContext(ProgressContext);
  const { isDocumentsRequiredForTraining } = useTrainingComplete();
  if (!setProgress) {
    throw new Error(
      'You must wrap your component in a `ProgressProvider` to use `useContext`'
    );
  }

  useEffect(() => {
    if (progress) return;

    training
      .getProgress()
      .then((progress) =>
        setProgress({
          ...progress,
          answers: parseJsonObject(progress.answers),
          results: parseJsonObject(progress.results),
        })
      )
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(t('form.errors.generic'), {
          variant: 'error',
        });
      });
  }, [progress, setProgress, enqueueSnackbar]);

  const updateProgress = (updates) => {
    setProgress({ ...progress, ...updates });
    training.updateProgress({ ...progress, ...updates });
  };

  const isLoading = !progress;
  const answers = progress?.answers ?? {};
  const section = useMemo(() => {
    return isDocumentsRequiredForTraining &&
      !user.is_required_documents_for_training_uploaded &&
      !['start', 'lesson', 'exam'].includes(progress?.section)
      ? 'docUpload'
      : (progress?.section ?? 'lesson');
  }, [progress, user]);

  const setAnswers = (updates) =>
    updateProgress({ answers: { ...answers, ...updates } });

  const setSection = (newSection) =>
    updateProgress({ section: newSection, page: 0 });

  const page = progress?.page;
  const setPage = (newPage) => {
    updateProgress({ page: newPage });
  };

  const results = progress?.results ?? {};
  const finishExam = (newResults) =>
    updateProgress({
      results: newResults,
      section: isDocumentsRequiredForTraining
        ? user.is_required_documents_for_training_uploaded
          ? 'review'
          : 'docUpload'
        : 'review',
      page: 0,
    });
  const restartExam = () =>
    updateProgress({ answers: {}, section: 'lesson', page: 0 });

  const finishProgram = async (initials) => {
    updateProgress({ initials });
    return training.finishProgram();
  };

  const clearAnswers = () => updateProgress({ answers: {} });

  return {
    isLoading,
    answers,
    setAnswers,
    page,
    setPage,
    section,
    setSection,
    results,
    finishExam,
    restartExam,
    finishProgram,
    clearAnswers,
  };
}
